import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LogoLockup } from "./Logo";
import {
  Activity,
  BarChart3,
  Bell,
  CandlestickChart,
  Database,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Moon,
  Plug,
  Menu,
  Radio,
  Settings,
  Sun,
  SunMoon,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { useState, useRef, useEffect, useMemo } from "react";
import { useAuth } from "../lib/auth";
import { useTheme, type ThemeMode } from "../lib/theme";
import ConnectivityBanner from "./ConnectivityBanner";
import IconButton from "./IconButton";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

/** Nav is filtered by the caller's own access.
 *
 *  Presentation only — `is_admin` and the feature flags come from localStorage
 *  and are trivially forged. Every one of these pages is independently gated
 *  server-side (`require_admin`, `policy.assert_feature`); hiding the link just
 *  avoids showing a page whose every action would 403. */
function buildNavItems(user: {
  is_admin?: boolean;
  can_backtest?: boolean;
  can_backfill?: boolean;
} | null): NavItem[] {
  const canBacktest = !!user?.is_admin || !!user?.can_backtest;
  const canBackfill = !!user?.is_admin || !!user?.can_backfill;
  return [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/chart", label: "Chart", icon: CandlestickChart },
    { to: "/deployments", label: "Deployments", icon: Activity },
    { to: "/strategies", label: "Strategies", icon: Settings },
    ...(canBacktest
      ? [{ to: "/backtests", label: "Backtests", icon: BarChart3 }]
      : []),
    { to: "/brokers", label: "Brokers", icon: Plug },
    ...(canBackfill ? [{ to: "/data", label: "Data", icon: Database }] : []),
    { to: "/settings/notifications", label: "Notifications", icon: Bell },
    ...(user?.is_admin
      ? [
          { to: "/admin/live", label: "Live now", icon: Radio },
          { to: "/admin/users", label: "Users", icon: UsersRound },
        ]
      : []),
  ];
}

export default function Layout() {
  const { user, clearSession } = useAuth();
  const navItems = useMemo(() => buildNavItems(user), [user]);
  // Mobile nav drawer. Closed on every route change so tapping a link does not
  // leave the overlay covering the page you just navigated to.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setNavOpen(false), [location.pathname]);
  // While the drawer is open the page behind it must not scroll under the
  // finger — without this, a swipe on the backdrop scrolls `main`.
  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [navOpen]);
  return (
    // h-screen + overflow-hidden, NOT min-h-screen. `main` is overflow-auto,
    // but overflow only creates a scroll container when the box's height is
    // bounded — under min-h-screen it isn't, so a page taller than the viewport
    // grew `main`, grew this wrapper, stretched the sidebar to match, and let
    // `nav flex-1` push the email + Log out block below the fold. Pinning the
    // shell to exactly one viewport makes `main` scroll internally instead.
    <div className="h-screen overflow-hidden flex bg-canvas text-fg">
      {/* Backdrop: only rendered on mobile, and only while the drawer is open.
          `lg:hidden` keeps it out of the way entirely on a desktop viewport. */}
      {navOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
        />
      )}
      {/* Below lg this is a fixed off-canvas drawer; from lg it reverts to the
          static column it has always been (`lg:static lg:translate-x-0`), so
          the desktop layout is byte-identical to before. */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 shrink-0 bg-surface border-r border-border
          flex flex-col overflow-hidden transition-transform duration-200 ease-out
          lg:static lg:z-auto lg:w-56 lg:translate-x-0
          ${navOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-5 py-5 border-b border-border">
          <LogoLockup />
          <div className="text-xs text-muted mt-1.5">Backtest · Deploy · Trade</div>
        </div>
        {/* min-h-0 lets this shrink below its content so the footer below it is
            never pushed out; overflow-y-auto then scrolls the links themselves
            on a short viewport rather than hiding Log out. */}
        <nav className="flex-1 min-h-0 overflow-y-auto py-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "bg-surface-2 border-brand-500 text-fg"
                      : "border-transparent text-fg-subtle hover:bg-surface-2 hover:text-fg"
                  }`
                }
              >
                <Icon size={16} aria-hidden />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="shrink-0 px-5 py-4 border-t border-border text-xs">
          <div className="text-fg-subtle truncate" title={user?.email}>
            {user?.email}
          </div>
          {user?.is_admin && (
            <div className="mt-1 inline-block px-1.5 py-0.5 bg-brand-600 text-white rounded text-[10px]">
              admin
            </div>
          )}
          <button
            onClick={clearSession}
            className="mt-3 flex items-center gap-1.5 text-fg-subtle hover:text-fg"
          >
            <LogOut size={12} /> Log out
          </button>
        </div>
      </aside>
      {/* min-w-0 is what stops a wide child (a table, the chart) from forcing
          this flex item past the viewport and squeezing the content column —
          the symptom was everything crammed into a narrow strip on a phone. */}
      <main className="flex-1 min-w-0 overflow-auto">
        <TopBar onOpenNav={() => setNavOpen(true)} />
        <ConnectivityBanner />
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function TopBar({ onOpenNav }: { onOpenNav: () => void }) {
  return (
    <div className="sticky top-0 z-20 h-12 px-3 sm:px-6 flex items-center gap-2 bg-canvas/85 backdrop-blur border-b border-border">
      <IconButton title="Menu" onClick={onOpenNav} size="sm" className="lg:hidden">
        <Menu size={18} />
      </IconButton>
      <div className="flex-1" />
      <ThemeToggle />
    </div>
  );
}

function ThemeToggle() {
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const Icon = mode === "dark" ? Moon : mode === "light" ? Sun : SunMoon;
  return (
    <div className="relative" ref={ref}>
      <IconButton
        title={`Theme: ${mode}`}
        onClick={() => setOpen((v) => !v)}
        size="sm"
      >
        <Icon size={16} />
      </IconButton>
      {open && (
        <div className="absolute right-0 mt-1 w-36 bg-surface border border-border rounded shadow-card text-sm overflow-hidden">
          {(["light", "dark", "system"] as ThemeMode[]).map((m) => (
            <button
              key={m}
              onClick={() => {
                setMode(m);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 hover:bg-surface-2 capitalize flex items-center gap-2 ${
                m === mode ? "text-brand-600 dark:text-brand-400 font-medium" : "text-fg"
              }`}
            >
              {m === "light" && <Sun size={14} />}
              {m === "dark" && <Moon size={14} />}
              {m === "system" && <SunMoon size={14} />}
              {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Suppress unused-import warning if a future revision drops `KeyRound`.
void KeyRound;
